import { useEffect, useRef, useState } from 'react';

import { Search } from 'lucide-react';

import { TranscriptInconsistencyComponent } from './inconsistency';
import { TranscriptInconsistency } from '@/api/types/backend-dto-types';

export function TranscriptInconsistencies({
  caseId,
  displayInconsistencies,
  isPlaybackMode,
  handleScrollToBlockFromInconsistency,
  numberOfBlocks,
}: {
  caseId: string;
  displayInconsistencies: TranscriptInconsistency[];
  isPlaybackMode: boolean;
  handleScrollToBlockFromInconsistency: (blockId: string) => void;
  numberOfBlocks: number;
}) {
  const [expandedInconsistencies, setExpandedInconsistencies] = useState<Set<string>>(new Set());
  const inconsistenciesContainerRef = useRef<HTMLDivElement>(null);

  const toggleInconsistencyExpansion = (inconsistencyId: string) => {
    setExpandedInconsistencies((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(inconsistencyId)) {
        newSet.delete(inconsistencyId);
      } else {
        newSet.add(inconsistencyId);
      }
      return newSet;
    });
  };

  // Auto-scroll to bottom when new inconsistencies are added
  useEffect(() => {
    if (inconsistenciesContainerRef.current && displayInconsistencies.length > 0) {
      const container = inconsistenciesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [displayInconsistencies.length]);

  return (
    <div ref={inconsistenciesContainerRef} className="flex h-full justify-center overflow-y-auto px-6 py-6">
      {numberOfBlocks === 0 ? (
        <div className="flex h-full max-w-sm items-center justify-center text-balance">
          <p className="text-center text-sm text-gray-500">Start recording to search for inconsistencies</p>
        </div>
      ) : !Array.isArray(displayInconsistencies) || displayInconsistencies.length === 0 ? (
        <div className="flex h-full max-w-sm items-center justify-center text-balance">
          {isPlaybackMode ? (
            <p className="text-center text-sm text-gray-500">No inconsistencies found in this transcript.</p>
          ) : (
            <p className="flex animate-pulse items-center gap-2 text-center text-sm text-gray-500">
              <Search size={16} />
              Searching for inconsistencies...
            </p>
          )}
        </div>
      ) : (
        <div className="relative">
          <div className="space-y-0">
            {displayInconsistencies.map((inconsistency) => (
              <TranscriptInconsistencyComponent
                key={inconsistency.id}
                inconsistency={inconsistency}
                caseId={caseId ?? ''}
                isExpanded={expandedInconsistencies.has(inconsistency.id)}
                onToggleExpansion={() => toggleInconsistencyExpansion(inconsistency.id)}
                onScrollToBlock={handleScrollToBlockFromInconsistency}
                isRecordingComplete={isPlaybackMode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
